import React, { useMemo, useCallback } from 'react';
import { EditRedistributionData } from '../editRedistributionLib';
import { Column } from '@ydistri/ds';
import { addToast } from '@ydistri/utils';
import { useCurrency } from '../../../../hooks/useCurrency';
import StatisticsSummary from '../StatisticsSummary';
import PrefilledTableSourceStore from '../../Statistics/modal/SourceStore/PrefilledTableSourceStore';
import { useApplicationData } from '../../../../hooks/useApplicationData';
import { useDeleteSourceStoresMutation } from '../apiCalculationEdit';
import { useAppDispatch } from '../../../../store';
import { closeModal } from '../../../Modals/modalSlice';
import { processErrorAndShowToast } from '../../../../lib/utils/processErrorAndShowToast';
import BackupOrRemoveButton from '../BackupOrRemoveButton';
import { buildSourceStoreStats } from './sourceStoreStats';
import { computeRemSize } from '@ydistri/theme';

type EditRedistributionRouteProps = Pick<
  EditRedistributionData,
  'calculationId' | 'sourceStores' | 'useDepartments'
>;

const EditRedistributionSourceStore: React.FC<EditRedistributionRouteProps> = ({
  calculationId,
  sourceStores,
  useDepartments,
}) => {
  const currency = useCurrency();
  const { departments: hasDepartments } = useApplicationData();
  const dispatch = useAppDispatch();
  const [deleteSourceStores, deleteSourceStoresStatus] = useDeleteSourceStoresMutation();

  const stats = useMemo(
    () => buildSourceStoreStats(sourceStores, hasDepartments, currency),
    [sourceStores, hasDepartments, currency],
  );

  const handleDelete = useCallback(() => {
    deleteSourceStores({
      calculationId,
      sourceStores: (sourceStores ?? []).map((r, i) => ({
        sourceStoreId: r.sourceStore.id,
        departmentId: r.department?.id,
        description: i === 0 ? `Removed ${sourceStores?.length} source stores` : undefined,
        entityDetail: JSON.stringify(r),
      })),
    })
      .then(() => {
        dispatch(addToast({ message: `Source store entries removed successfully.` }));
        dispatch(closeModal());
      })
      .catch(processErrorAndShowToast(dispatch, 'Removing source stores failed'));
  }, [deleteSourceStores, dispatch, calculationId, sourceStores]);

  return (
    <Column $gap="2rem" $justifyContent="space-evenly" $padding={0}>
      <div style={{ minHeight: computeRemSize(400) }}>
        <PrefilledTableSourceStore
          data={sourceStores}
          displayDepartment={hasDepartments && useDepartments}
        />
      </div>
      <StatisticsSummary title="Summary" stats={stats} />
      <BackupOrRemoveButton
        calculationId={calculationId}
        loading={deleteSourceStoresStatus.isLoading}
        onClick={handleDelete}
      />
    </Column>
  );
};

export default EditRedistributionSourceStore;
